/* eslint-disable enforce-ids-in-jsx/missing-ids */
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { ProductAddedEvent } from '@delta-defense/frontend-analytics/events/module';
import { ITransaction, membershipBenefits, Transaction } from '../../core/domain/module';
import { TransactionFieldProps } from './TransactionField';
import ValidationMessage from '../ValidationMessage/ValidationMessage';
import { Levels } from '../../core/enums/levels';
import { Icons } from '../../icons';
import { getProductsForAnalytics } from '../../utilities/getProductsForAnalytics';
import { IAnalytics, Analytics } from '../../core/services/analytics/analytics';
import { scrollElementIntoView } from '../../utilities/scrollElementIntoView';
import styles from './SimpleLevel.module.scss';

let timeout = 0;
const debounceProductAddedEvent = (transaction: ITransaction, analytics: IAnalytics) => {
	if (timeout) {
		clearTimeout(timeout);
	}
	timeout = setTimeout(() => {
		const products = getProductsForAnalytics({
			primary: transaction.Data.purchaseProducts?.primary
		});
		analytics.TrackEvent(new ProductAddedEvent(products[0]));
	}, timeout) as unknown as number;
};

export const SimpleLevel: FunctionComponent<TransactionFieldProps> = ({
	field,
	showValidation,
	changeHandler,
	defaultValue,
	useEffectFunc = useEffect,
	transaction = Transaction.Instance(),
	analytics = Analytics.Instance()
}) => {
	const [currentLevel, setCurrentLevel] = useState<string | undefined>();

	useEffectFunc(() => {
		setCurrentLevel(defaultValue);
	}, []);

	useEffectFunc(() => {
		if (currentLevel) {
			field.SetValue(currentLevel);
			transaction.Updates.Publish(true);
			debounceProductAddedEvent(transaction, analytics);
		}
	}, [currentLevel]);

	const [expanded, setExpanded] = useState<Levels | null>(null);

	return (
		<>
			<section className={styles.chooseLevel}>
				<div className={styles.accordion}>
					<LevelAccordion
						level={Levels.Gold}
						checked={currentLevel === Levels.Gold}
						expanded={expanded === Levels.Gold}
						setExpanded={setExpanded}
						setCurrentLevel={setCurrentLevel}
						onLevelChange={changeHandler}
					/>
					<LevelAccordion
						level={Levels.Platinum}
						checked={currentLevel === Levels.Platinum}
						expanded={expanded === Levels.Platinum}
						setExpanded={setExpanded}
						setCurrentLevel={setCurrentLevel}
						onLevelChange={changeHandler}
					/>
					<LevelAccordion
						level={Levels.Elite}
						checked={currentLevel === Levels.Elite}
						expanded={expanded === Levels.Elite}
						setExpanded={setExpanded}
						setCurrentLevel={setCurrentLevel}
						onLevelChange={changeHandler}
					/>
				</div>
			</section>
			{showValidation && <ValidationMessage>{field.ValidationMessage}</ValidationMessage>}
		</>
	);
};

type AccordionProps = {
	level: Levels;
	checked: boolean;
	expanded: boolean;
	setExpanded: React.Dispatch<React.SetStateAction<Levels | null>>;
	setCurrentLevel: React.Dispatch<React.SetStateAction<string | undefined>>;
	onLevelChange?: React.ChangeEventHandler<HTMLElement>;
};

const LevelAccordion: FunctionComponent<AccordionProps> = ({ level, checked, expanded, setExpanded, onLevelChange, setCurrentLevel }) => {
	const inputRef = useRef<HTMLInputElement>(null);
	return (
		<div className={`${styles.accordionItem} ${level}`}>
			<button
				onClick={(e) => {
					e.preventDefault();
					setExpanded(expanded ? null : level);
				}}
				className={styles.accordionHeading}
			>
				<div className={styles.radioButtonWrapper}>
					<input
						type="radio"
						ref={inputRef}
						id={level}
						name="level"
						value={level}
						className={styles.radioButton}
						checked={checked}
						onChange={onLevelChange}
						onClick={(e) => {
							e.stopPropagation();
							e.preventDefault();
							setCurrentLevel(level);
						}}
					/>
					<label htmlFor={level} className={styles.membershipLabel}>
						<span className={`${styles.membershipLevel} ${styles[`membershipLevel__${level}`]}`}>{level} Membership</span>
						<span className={styles.membershipPrice}>
							${membershipBenefits[level].monthlyPrice}
							<span>/month</span>
						</span>
					</label>
				</div>
				<span className={`${styles.accordionArrow} ${expanded ? styles.rotate : ''}`}></span>
			</button>
			<div className={`${styles.accordionContent} ${expanded ? styles.updateHeight : ''}`}>
				<div className={styles.accordionContentWrapper}>
					<div className={styles.contentColumn}>
						{Object.values(membershipBenefits[level].benefitSections).map((section) => (
							<div className={styles.benefit}>
								<h2 className={styles.benefitHeading}>{section.heading}</h2>
								<hr className={styles.benefitDivider} />
								<p className={styles.benefitSubHeading}>{section.subheading}</p>
								<ul className={styles.benefitList}>
									{section.benefits.map(({ label, value }, i) => (
										<li className={styles.benefitListItem} key={i}>
											<span className={styles.benefitListItemLabel}>{label}</span>
											<span className={`${styles.benefitListItemValue} ${styles[`benefitListItemValue__${level}`]}`}>
												{typeof value === 'string' ? value : value ? Icons.CheckHardEdges : 'X'}
											</span>
										</li>
									))}
								</ul>
							</div>
						))}
						<button
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								if (inputRef.current) {
									inputRef.current.checked = true;
									setCurrentLevel(level);
									inputRef.current.dispatchEvent(new Event('change'));
									scrollElementIntoView(`input#${level}`, { block: 'center' });
								}
								setTimeout(() => {
									setExpanded(null);
								}, 500);
							}}
						>
							Select {level}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};
